import React, { useEffect, useRef } from "react";
import { useEditor, useEditorState, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Placeholder from "@tiptap/extension-placeholder";
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  Strikethrough,
  List,
  ListOrdered,
  Quote,
  Link2,
  Unlink,
  Heading2,
  Undo2,
  Redo2,
  RemoveFormatting,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface RichTextEditorProps {
  /** HTML content */
  value: string;
  onChange: (html: string, text: string) => void;
  placeholder?: string;
  className?: string;
  minHeight?: number;
  disabled?: boolean;
  /** Called when the box gets focus, with its editor (e.g. to insert merge fields at the cursor) */
  onFocusEditor?: (editor: Editor) => void;
}

/**
 * Email-style rich text box (TipTap). Emits HTML plus a plain-text version.
 * `value` is applied when it changes from outside (template picked, dialog reopened).
 */
export function RichTextEditor({ value, onChange, placeholder, className, minHeight = 220, disabled = false, onFocusEditor }: RichTextEditorProps) {
  const onFocusRef = useRef(onFocusEditor);
  onFocusRef.current = onFocusEditor;
  const editor = useEditor({
    editable: !disabled,
    extensions: [
      StarterKit.configure({
        heading: { levels: [2, 3] },
        codeBlock: false,
        code: false,
        horizontalRule: false,
        link: { openOnClick: false, autolink: true, defaultProtocol: "https" },
      }),
      Placeholder.configure({ placeholder: placeholder || "Write your message…" }),
    ],
    content: value,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: "rte-content focus:outline-none px-3 py-2.5 text-sm leading-relaxed",
        style: `min-height:${minHeight}px`,
      },
    },
    onUpdate: ({ editor }) => onChange(editor.getHTML(), editor.getText({ blockSeparator: "\n\n" })),
    onFocus: ({ editor }) => onFocusRef.current?.(editor),
  });

  useEffect(() => {
    if (editor && editor.isEditable === disabled) editor.setEditable(!disabled, false);
  }, [editor, disabled]);

  // Sync content set from outside without fighting the user's typing
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
  }, [editor, value]);

  return (
    <div
      className={cn(
        "rounded-md border border-input bg-background shadow-xs focus-within:ring-[3px] focus-within:ring-ring/50 focus-within:border-ring",
        className
      )}
    >
      {editor && !disabled && <Toolbar editor={editor} />}
      <div className="max-h-[50vh] overflow-y-auto">
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  // Re-render the toolbar when the selection's formatting changes
  const state = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"),
      italic: e.isActive("italic"),
      underline: e.isActive("underline"),
      strike: e.isActive("strike"),
      heading: e.isActive("heading", { level: 2 }),
      bullet: e.isActive("bulletList"),
      ordered: e.isActive("orderedList"),
      quote: e.isActive("blockquote"),
      link: e.isActive("link"),
      canUndo: e.can().undo(),
      canRedo: e.can().redo(),
    }),
  });

  const setLink = () => {
    const previous = editor.getAttributes("link").href as string | undefined;
    const url = window.prompt("Link address", previous || "https://");
    if (url === null) return;
    if (!url.trim() || url.trim() === "https://") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    const href = /^(https?:|mailto:|tel:)/i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  };

  const buttons: { icon: React.ElementType; label: string; active?: boolean; disabled?: boolean; run: () => void; divider?: boolean }[] = [
    { icon: Bold, label: "Bold (Ctrl+B)", active: state.bold, run: () => editor.chain().focus().toggleBold().run() },
    { icon: Italic, label: "Italic (Ctrl+I)", active: state.italic, run: () => editor.chain().focus().toggleItalic().run() },
    { icon: UnderlineIcon, label: "Underline (Ctrl+U)", active: state.underline, run: () => editor.chain().focus().toggleUnderline().run() },
    { icon: Strikethrough, label: "Strikethrough", active: state.strike, run: () => editor.chain().focus().toggleStrike().run() },
    { icon: Heading2, label: "Heading", active: state.heading, divider: true, run: () => editor.chain().focus().toggleHeading({ level: 2 }).run() },
    { icon: List, label: "Bulleted list", active: state.bullet, run: () => editor.chain().focus().toggleBulletList().run() },
    { icon: ListOrdered, label: "Numbered list", active: state.ordered, run: () => editor.chain().focus().toggleOrderedList().run() },
    { icon: Quote, label: "Quote", active: state.quote, run: () => editor.chain().focus().toggleBlockquote().run() },
    { icon: Link2, label: "Add link", active: state.link, divider: true, run: setLink },
    { icon: Unlink, label: "Remove link", disabled: !state.link, run: () => editor.chain().focus().unsetLink().run() },
    { icon: RemoveFormatting, label: "Clear formatting", run: () => editor.chain().focus().unsetAllMarks().clearNodes().run() },
    { icon: Undo2, label: "Undo (Ctrl+Z)", disabled: !state.canUndo, divider: true, run: () => editor.chain().focus().undo().run() },
    { icon: Redo2, label: "Redo (Ctrl+Y)", disabled: !state.canRedo, run: () => editor.chain().focus().redo().run() },
  ];

  return (
    <div className="flex flex-wrap items-center gap-0.5 border-b border-input px-1.5 py-1 bg-muted/30 rounded-t-md">
      {buttons.map((b) => (
        <React.Fragment key={b.label}>
          {b.divider && <span className="w-px h-5 bg-border mx-1" />}
          <button
            type="button"
            title={b.label}
            aria-label={b.label}
            aria-pressed={b.active}
            disabled={b.disabled}
            onMouseDown={(e) => e.preventDefault()}
            onClick={b.run}
            className={cn(
              "size-7 inline-flex items-center justify-center rounded text-muted-foreground hover:bg-accent hover:text-foreground disabled:opacity-40 disabled:pointer-events-none",
              b.active && "bg-primary/15 text-primary"
            )}
          >
            <b.icon className="size-4" />
          </button>
        </React.Fragment>
      ))}
    </div>
  );
}

export default RichTextEditor;
